import { Component, OnInit, ViewChildren, QueryList } from '@angular/core';
import { DataAccess } from '../../data-access/services/data-access.service';
import { FormGroup, FormBuilder, FormArray, FormControl } from '@angular/forms';
import { MatExpansionPanel, MatChipInputEvent } from '@angular/material';
import {COMMA, ENTER, P} from '@angular/cdk/keycodes';

@Component({
  selector: 'bm-methods-editor',
  templateUrl: './methods-editor.component.html',
  styleUrls: ['./methods-editor.component.scss']
})
export class MethodsEditorComponent implements OnInit {

  readonly separators: number[] = [ENTER, COMMA];
  rowData: any[] = [];
  methodsEditorForm: FormGroup;

  formFieldTypes = [
    'string',
    'number',
    'bool',
    'date',
    'daterange',
    'time',
    'autocomplete_Country',
    'autocomplete_Currency',
    'autocomplete_Processor',
    'enum_Country',
    'enum_Currency',
    'enum_Processor',
  ];


  @ViewChildren(MatExpansionPanel)
  panels: QueryList<MatExpansionPanel>;

  constructor(private dcs: DataAccess, private builder: FormBuilder) {}

  collapseAll() {
    this.panels.forEach(panel => panel.close())
  }

  expandAll() {
    this.panels.forEach(panel => panel.open())
  }

  createPrimaryControl(): FormControl {
    return this.builder.control('');
  }

  createPrimaryGroup(): FormGroup {
    return this.builder.group({
      key: '',
      value: '',
    });
  }

  createRole(): FormGroup {
    return this.builder.group({
      roleType: '',
      targetValue: '',
      styleType: '',
      styleValue: '',
    });
  }

  createDefinition(): FormGroup {
    return this.builder.group({
      colName: '',
      colType: '',
      openWith: '',
      formName: '',
      action: '',
      resCells: this.builder.array([]),

      role: this.builder.array([
        this.createRole()
      ]),
    });
  }

  createGlobalDefinition(): FormGroup {
    return this.builder.group({
      hideCols: this.builder.array([]),
      summarizeCols: this.builder.array([]),
    });
  }

  createCollection(): FormArray {
    return this.builder.array([
      this.createDefinition(),
      this.createGlobalDefinition(),
    ]);
  }

  ngOnInit() {

    this.dcs.execute('SettingMethods')
    .then(response => {
      this.rowData = response.data[0];
    });

    this.methodsEditorForm = this.builder.group({

      id: [''],
      actionName: [''],
      schemaName: [''],
      spName: [''],
      cacheTimeInSec: [''],
      description: [''],
      isActive: [''],

      searchBy: this.builder.array([
        this.createPrimaryGroup(),
      ]),

      extensions: this.builder.array([
        this.createPrimaryGroup(),
      ]),

      additionalInfo: this.builder.array([
        this.createCollection(),
      ])

    });

  }

  get searchBy() {
    return this.methodsEditorForm.get('searchBy') as FormArray;
  }

  get extensions() {
    return this.methodsEditorForm.get('extensions') as FormArray;
  }

  get additionalInfo() {
    return this.methodsEditorForm.get('additionalInfo') as FormArray;
  }

  clear() {
    this.methodsEditorForm.reset();
  }


  updateEditor(data) {


    data = {...data};

    data.searchBy = this.jsonToFormArray(data.searchBy);
    data.extensions = this.jsonToFormArray(data.extensions);
    data.additionalInfo = this.additionalInfoToFormArray(data.additionalInfo);

    this.searchBy.clear();
    this.extensions.clear();
    this.additionalInfo.clear();

    while (this.searchBy.length < data.searchBy.length) {
      this.searchBy.push(this.createPrimaryGroup());
    }

    while (this.extensions.length < data.extensions.length) {
      this.extensions.push(this.createPrimaryGroup());
    }

    for (const collection of data.additionalInfo) {
      const globalDefinition = collection.pop();
      const collectionControl = this.createCollection();
      collectionControl.clear();

      for (const definition of collection) {
        const definitionControl = this.createDefinition();
        const resCells = definitionControl.get('resCells') as FormArray;
        const roles = definitionControl.get('role') as FormArray;
        resCells.clear();
        roles.clear();

        while (resCells.length < definition.resCells.length) {
          resCells.push(this.createPrimaryControl());
        }

        while (roles.length < definition.role.length) {
          roles.push(this.createRole());
        }

        collectionControl.push(definitionControl);
      }

      collection.push(globalDefinition);

      const globalDefinitionControl = this.createGlobalDefinition();
      const hideCols = globalDefinitionControl.get('hideCols') as FormArray;
      const summarizeCols = globalDefinitionControl.get('summarizeCols') as FormArray;
      hideCols.clear();
      summarizeCols.clear();

      while (hideCols.length < globalDefinition.hideCols.length) {
        hideCols.push(this.createPrimaryControl());
      }

      while (summarizeCols.length < globalDefinition.summarizeCols.length) {
        summarizeCols.push(this.createPrimaryControl());
      }

      collectionControl.push(globalDefinitionControl);

      this.additionalInfo.push(collectionControl);
    }

    this.methodsEditorForm.patchValue(data);
  }

  appendItem(control: FormArray, event: MatChipInputEvent) {
    const {input, value} = event;

    if ((value || '').trim()) {
      control.push(this.builder.control(value.trim()));
    }

    if (input) {
      input.value = '';
    }
  }

  async handleSave() {

    const result = {...this.methodsEditorForm.value};
    result.searchBy = this.formArrayToJson(result.searchBy);
    result.extensions = this.formArrayToJson(result.extensions);
    result.additionalInfo = this.additionalInfoToJson(result.additionalInfo);

    const obj = {};

    result.additionalInfo.forEach((info, index) => {
      obj['res' + index] = info;
    });

    result.additionalInfo = result.additionalInfo.length ? obj : null;

    const response = await this.dcs.execute('UpdateSettingMethods', result);

    this.rowData = response.data[0];
  }

  formArrayToJson(array: {key: string, value: string}[]) {

    array = array.filter(item => item.key && item.value);
    if (!array.length) {
      return null;
    }

    const json = {};
    array.forEach(pair => {
      json[pair.key] = pair.value;
    });

    return json;
  }

  additionalInfoToJson(array: any[]) {

    if (!array.length) {
      return null;
    }

    array = array.slice();

    const collections = [];

    for (const collection of array) {

      const defs = [];

      for (let i = 0; i < collection.length; i++) {

        const def = collection[i];

        if (i !== collection.length - 1) {

          if (def.colName) {

            const roles = [];

            for (const role of def.role) {
              if (role.roleType && role.targetValue && role.styleType && role.styleValue) {
                roles.push({
                    [role.roleType]: role.targetValue,
                    [role.styleType]: role.styleValue,
                });
              }
            }

            def.role = roles;
            defs.push(def);
          }
        } else if (def.hideCols.length || def.summarizeCols.length || defs.length) {
          defs.push(def);
        }

      }

      if (defs.length) {
        collections.push(defs);
      }

    }

    return collections;
  }

  jsonToFormArray(json: any) {


    if (!json) {
      return [];
    }

    return Object.entries(JSON.parse(json)).map(([key, value]) => {
      return {key, value};
    });
  }

  additionalInfoToFormArray(json: any) {
    if (!json) {
      return [];
    }

    const additionalInfo = Object.values(JSON.parse(json));

    additionalInfo.forEach((collection: any) => {
      collection.forEach((definition, index) => {
        if (index !== collection.length - 1) {

          definition.role = definition.role || [];
          definition.role.forEach((role, ind) => {
            const [roleType, styleType] = Object.keys(role);
            const [targetValue, styleValue] = Object.values(role);
            definition.role[ind] = {roleType, styleType, targetValue, styleValue};
          });
        }
      });
    });

    return additionalInfo;

  }

}
